import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { companyApi } from '@/services/company';
import {
    UpdateCompanyProfileRequest,
    UpdateCompanyProfileResponse,
    AddTeamMembersRequest,
    AddTeamMembersResponse,
    GetCompanyDataResponse,
    CompanyConfigurationRequest,
    CompanyConfigurationResponse,
    GetRolesResponse,
    CompanyElementsRequest,
    CompanyElementsResponse,
    OnboardingStatusResponse,
    CompleteOnboardingResponse,
    UploadLogoResponse,
    InviteTeamMemberRequest,
    InviteTeamMemberResponse,
    GetUserDetailsResponse,
    UpdateUserRequest,
    UpdateUserResponse
} from '@/types/api';
import { ApiError } from '@/lib/api-client';

// Company logo upload mutation hook
export const useUploadLogo = () => {
    const queryClient = useQueryClient();

    return useMutation<
        UploadLogoResponse,
        ApiError,
        { companyId: string; file: File }
    >({
        mutationFn: ({ companyId, file }) => companyApi.uploadLogo(companyId, file),
        onSuccess: (data, variables) => {
            console.log('Logo upload successful:', data);
            // Invalidate company data to refetch updated logo URL
            queryClient.invalidateQueries({ queryKey: ['company', variables.companyId] });
        },
        onError: (error) => {
            console.error('Logo upload failed:', error);
        },
    });
};

// Company profile update mutation hook
export const useUpdateCompanyProfile = () => {
    return useMutation<
        UpdateCompanyProfileResponse,
        ApiError,
        { companyId: string; data: UpdateCompanyProfileRequest }
    >({
        mutationFn: ({ companyId, data }) => companyApi.updateProfile(companyId, data),
        onSuccess: (data) => {
            console.log('Company profile update successful:', data);
        },
        onError: (error) => {
            console.error('Company profile update failed:', error);
        },
    });
};

// Add team members mutation hook
export const useAddTeamMembers = () => {
    return useMutation<
        AddTeamMembersResponse,
        ApiError,
        { companyId: string; data: AddTeamMembersRequest }
    >({
        mutationFn: ({ companyId, data }) => companyApi.addTeamMembers(companyId, data),
        onSuccess: (data) => {
            console.log('Team members added successfully:', data);
        },
        onError: (error) => {
            console.error('Adding team members failed:', error);
        },
    });
};

// Update team member roles mutation hook
export const useUpdateTeamMemberRoles = () => {
    const queryClient = useQueryClient();

    return useMutation<
        void,
        ApiError,
        { userId: string; roleIds: string[] }
    >({
        mutationFn: ({ userId, roleIds }) => companyApi.updateTeamMemberRoles(userId, roleIds),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['user-details', variables.userId] });
            queryClient.invalidateQueries({ queryKey: ['team-members'], exact: false });
        },
        onError: (error) => {
            console.error('Updating team member roles failed:', error);
        },
    });
};

// Invite team member mutation hook
export const useInviteTeamMember = () => {
    const queryClient = useQueryClient();

    return useMutation<
        InviteTeamMemberResponse,
        ApiError,
        { companyId: string; data: InviteTeamMemberRequest }
    >({
        mutationFn: ({ companyId, data }) => companyApi.inviteTeamMember(companyId, data),
        onSuccess: (data, variables) => {
            console.log('Team member invited successfully:', data);
            // Invalidate both old and new team members queries to refetch updated list
            queryClient.invalidateQueries({ queryKey: ['company', variables.companyId, 'teamMembers'] });
            queryClient.invalidateQueries({ queryKey: ['team-members', variables.companyId] });
        },
        onError: (error) => {
            console.error('Inviting team member failed:', error);
        },
    });
};

// Resend invitation mutation hook
export const useResendInvitation = () => {
    const queryClient = useQueryClient();

    return useMutation<
        { message: string; success: boolean },
        ApiError,
        { companyId: string; data: { userId: string } }
    >({
        mutationFn: ({ companyId, data }) => companyApi.resendInvitation(companyId, data),
        onSuccess: (data, variables) => {
            console.log('Invitation resent successfully:', data);
            // Invalidate team members queries to refetch updated list
            queryClient.invalidateQueries({ queryKey: ['company', variables.companyId, 'teamMembers'] });
            queryClient.invalidateQueries({ queryKey: ['team-members', variables.companyId] });
        },
        onError: (error) => {
            console.error('Resending invitation failed:', error);
        },
    });
};

// Admin-initiated password reset mutation hook (Colleagues → Login tab)
export const useResetTeamMemberPassword = () => {
    return useMutation<
        { success: boolean; message: string },
        ApiError,
        { userId: string }
    >({
        mutationFn: ({ userId }) => companyApi.resetTeamMemberPassword(userId),
        onError: (error) => {
            console.error('Resetting team member password failed:', error);
        },
    });
};

// Get company data query hook
export const useGetCompanyData = (companyId: string | null, tabName: string) => {
    return useQuery<GetCompanyDataResponse, ApiError>({
        queryKey: ['company', companyId, tabName],
        queryFn: () => companyApi.getCompanyData(companyId!, tabName),
        enabled: !!companyId, // Only run query when companyId is available
    });
};

// Company configuration update mutation hook
export const useUpdateCompanyConfiguration = () => {
    const queryClient = useQueryClient();

    return useMutation<
        CompanyConfigurationResponse,
        ApiError,
        { companyId: string; data: CompanyConfigurationRequest }
    >({
        mutationFn: ({ companyId, data }) => companyApi.updateConfiguration(companyId, data),
        onSuccess: (data, variables) => {
            console.log('Company configuration update successful:', data);
            // Invalidate company data to refetch updated configuration
            queryClient.invalidateQueries({ queryKey: ['company', variables.companyId] });
        },
        onError: (error) => {
            console.error('Company configuration update failed:', error);
        },
    });
};

// Company elements update mutation hook
export const useUpdateCompanyElements = () => {
    return useMutation<
        CompanyElementsResponse,
        ApiError,
        { companyId: string; data: CompanyElementsRequest }
    >({
        mutationFn: ({ companyId, data }) => companyApi.updateElements(companyId, data),
        onSuccess: (data) => {
            console.log('Company elements update successful:', data);
        },
        onError: (error) => {
            console.error('Company elements update failed:', error);
        },
    });
};

// Get roles query hook
export const useGetRoles = () => {
    return useQuery<GetRolesResponse, ApiError>({
        queryKey: ['roles'],
        queryFn: () => companyApi.getRoles(),
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

// Get onboarding status query hook
export const useGetOnboardingStatus = (companyId: string | null) => {
    return useQuery<OnboardingStatusResponse, ApiError>({
        queryKey: ['onboarding-status', companyId],
        queryFn: () => companyApi.getOnboardingStatus(companyId!),
        enabled: !!companyId, // Only run query when companyId is available
        staleTime: 30 * 1000, // 30 seconds - we want fresh data for onboarding status
    });
};

export const useCompleteOnboarding = () => {
    const queryClient = useQueryClient();

    return useMutation<CompleteOnboardingResponse, ApiError, string>({
        mutationFn: (companyId: string) => companyApi.completeOnboarding(companyId),
        onSuccess: (data, companyId) => {
            console.log('Onboarding completed successfully:', data);
            // Invalidate onboarding status to refetch updated data
            queryClient.invalidateQueries({ queryKey: ['onboarding-status', companyId] });
        },
        onError: (error) => {
            console.error('Failed to complete onboarding:', error);
        }
    });
};

// Get user details query hook
export const useGetUserDetails = (userId: string | null) => {
    return useQuery<GetUserDetailsResponse, ApiError>({
        queryKey: ['user-details', userId],
        queryFn: () => companyApi.getUserDetails(userId!),
        enabled: !!userId, // Only run query when userId is available
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

// Get team members with search and pagination
export const useGetTeamMembers = (params: {
    organizationId: string | null;
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    roleName?: string;
    verified?: boolean;
    memberStatus?: 'ACTIVE' | 'INACTIVE' | 'PENDING';
}) => {
    return useQuery({
        queryKey: ['team-members', params.organizationId, params.page, params.limit, params.search, params.sortBy, params.sortOrder, params.roleName, params.verified, params.memberStatus],
        queryFn: () => companyApi.getTeamMembers({
            organizationId: params.organizationId!,
            page: params.page,
            limit: params.limit,
            search: params.search,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            roleName: params.roleName,
            verified: params.verified,
            memberStatus: params.memberStatus,
        }),
        enabled: !!params.organizationId, // Only run query when organizationId is available
        staleTime: 30 * 1000, // 30 seconds - shorter than clients since team data changes more frequently
    });
};

// Deactivate team member mutation hook
export const useRemoveTeamMember = () => {
    const queryClient = useQueryClient();

    return useMutation<
        {
            requiresReassignment: boolean;
            message: string;
            deactivatedUser?: {
                id: string;
                fullName: string;
                email: string;
                roles: string[];
            };
            assignedClients?: { id: string; fullName: string }[];
            clientsReassigned?: number;
            reassignedTo?: string | null;
            userId?: string;
            fullName?: string;
        },
        ApiError,
        { companyId: string; userId: string; reassignToUserId?: string }
    >({
        mutationFn: ({ companyId, userId, reassignToUserId }) =>
            companyApi.removeTeamMember(companyId, userId, reassignToUserId),
        onSuccess: (data, variables) => {
            if (!data.requiresReassignment) {
                console.log('Team member deactivated successfully:', data);
                queryClient.invalidateQueries({ queryKey: ['team-members', variables.companyId] });
                queryClient.invalidateQueries({ queryKey: ['company', variables.companyId, 'teamMembers'] });
                queryClient.invalidateQueries({ queryKey: ['user-details', variables.userId] });
            }
        },
        onError: (error) => {
            console.error('Deactivating team member failed:', error);
        },
    });
};

// Get eligible reassignment targets query hook
export const useGetReassignmentTargets = (companyId: string | null, userId: string | null) => {
    return useQuery<{
        id: string;
        fullName: string;
        email: string;
        roles: string[];
        assignedClientCount: number;
    }[], ApiError>({
        queryKey: ['reassignment-targets', companyId, userId],
        queryFn: () => companyApi.getReassignmentTargets(companyId!, userId!),
        enabled: !!companyId && !!userId,
        staleTime: 30 * 1000,
    });
};

// Update user mutation hook
export const useUpdateUser = () => {
    const queryClient = useQueryClient();

    return useMutation<
        UpdateUserResponse,
        ApiError,
        { userId: string; data: UpdateUserRequest }
    >({
        mutationFn: ({ userId, data }) => companyApi.updateUser(userId, data),
        onSuccess: (data, variables) => {
            console.log('User update successful:', data);
            // Invalidate and refetch user details
            queryClient.invalidateQueries({ queryKey: ['user-details', variables.userId] });
            // Invalidate team members list so summary page reflects changes (prefix match)
            queryClient.invalidateQueries({ queryKey: ['team-members'], exact: false });
        },
        onError: (error) => {
            console.error('Update user failed:', error);
        },
    });
};

// Assign roles mutation hook
export const useAssignRoles = () => {
    const queryClient = useQueryClient();

    return useMutation<
        { message: string; success: boolean },
        ApiError,
        { userId: string; roleIds: string[] }
    >({
        mutationFn: ({ userId, roleIds }) => companyApi.assignRoles({ userId, roleIds }),
        onSuccess: (data, variables) => {
            console.log('Role assignment successful:', data);
            // Invalidate and refetch user details
            queryClient.invalidateQueries({ queryKey: ['user-details', variables.userId] });
            // Invalidate team members list so summary page reflects changes (prefix match)
            queryClient.invalidateQueries({ queryKey: ['team-members'], exact: false });
        },
        onError: (error) => {
            console.error('Assign roles failed:', error);
        },
    });
};

// Get company owner query hook
export const useGetCompanyOwner = (companyId: string | null) => {
    return useQuery<{ id: string; fullName: string; email: string; phone: string | null; createdAt: string }, ApiError>({
        queryKey: ['company-owner', companyId],
        queryFn: () => companyApi.getCompanyOwner(companyId!),
        enabled: !!companyId,
        staleTime: 5 * 60 * 1000,
    });
};

// Update company owner mutation hook
export const useUpdateCompanyOwner = () => {
    const queryClient = useQueryClient();

    return useMutation<
        { message: string; owner: any },
        ApiError,
        { companyId: string; data: { fullName?: string; phone?: string } }
    >({
        mutationFn: ({ companyId, data }) => companyApi.updateCompanyOwner(companyId, data),
        onSuccess: (data, variables) => {
            console.log('Company owner update successful:', data);
            queryClient.invalidateQueries({ queryKey: ['company-owner', variables.companyId] });
        },
        onError: (error) => {
            console.error('Company owner update failed:', error);
        },
    });
};